import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { buildQueryParams } from "../lib/globalUtils";
import { paramsToSend } from "../constants/userConstants";

export const useUserDetail = (userId, initialUser = null) => {
  const queryClient = useQueryClient();

  // Use React Query for caching and background updates
  const query = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        // Use the same params as when fetching all users
        const queryString = buildQueryParams(paramsToSend);
        const response = await apiClient.get(
          `/users/${userId}${queryString ? `?${queryString}` : ""}`,
        );
        const userData = response.result;

        // Sort wallets by ID if they exist
        if (userData && userData.wallets && Array.isArray(userData.wallets)) {
          userData.wallets = [...userData.wallets].sort((a, b) => a.id - b.id);
        }

        return userData;
      } catch (err) {
        throw new Error(
          err.response?.data?.message || "Failed to fetch user details",
        );
      }
    },
    enabled: !!userId && isEndpointEnabled("dashboard", "users"),
    staleTime: 0, // Always consider data stale to allow updates
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    initialData: initialUser, // Use initial data if provided
  });

  return {
    user: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    // Helper function to update user data optimistically
    updateUser: (updater) => {
      if (typeof updater === "function") {
        // Update the cache with the new data
        queryClient.setQueryData(["user", userId], (oldData) => {
          const updatedData = updater(oldData);
          // Ensure wallets are sorted after update
          if (
            updatedData &&
            updatedData.wallets &&
            Array.isArray(updatedData.wallets)
          ) {
            updatedData.wallets = [...updatedData.wallets].sort(
              (a, b) => a.id - b.id,
            );
          }
          return updatedData;
        });
      } else {
        // If it's an object, merge it with existing data
        queryClient.setQueryData(["user", userId], (oldData) => {
          const mergedData = {
            ...oldData,
            ...updater,
          };
          // Ensure wallets are sorted after merge
          if (
            mergedData &&
            mergedData.wallets &&
            Array.isArray(mergedData.wallets)
          ) {
            mergedData.wallets = [...mergedData.wallets].sort(
              (a, b) => a.id - b.id,
            );
          }
          return mergedData;
        });
      }
    },
  };
};
